import React from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Modal } from 'src/components/design-system';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { formatNumber } from 'shared/utils';
import styles from './knowledge-owner-popup.module.scss';

/**
 * Knowledge Owner Popup Component
 *
 * A reusable dialog for updating ownership of knowledge items. Supports both single
 * item ownership update and bulk ownership update with automatic UI adaptation.
 *
 * Features:
 * - Single item: Shows item title in confirmation message
 * - Bulk update: Shows count of items (never shows individual names)
 * - Custom bulk update handler support
 * - Loading state management
 * - Internationalization support
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object|Array<{id: string}>} props.entity - Single entity object with id and title, or array of objects with IDs for bulk update
 * @param {Function} props.onFinish - Callback fired when the popup closes (both success and dismiss)
 * @param {Function} [props.onUpdate] - Callback fired after successful ownership update
 * @param {Function} [props.onDismiss] - Callback fired when user cancels the operation
 * @param {Function} [props.onBulkUpdate] - Optional custom bulk update handler. If provided, this will be called instead of default bulk update logic
 * @returns {JSX.Element|null} The ownership update popup or null if no entity provided
 */
const KnowledgeOwnerPopup = React.memo(function KnowledgeOwnerPopup({
  open = false,
  entity,
  onFinish,
  onUpdate = () => {},
  onDismiss = () => {},
  onBulkUpdate = null,
}) {
  const lang = useSelector(getLang('OWNERSHIP_UPDATE'));
  const [isLoading, setLoadingState] = React.useState(false);
  const [selectedOwners, setSelectedOwners] = React.useState([]);

  // Determine if this is a bulk operation
  /** @type {boolean} Whether this is a bulk ownership update operation */
  const isBulkUpdate = Array.isArray(entity);
  /** @type {number} Number of items to be updated */
  const itemCount = isBulkUpdate ? entity.length : 1;

  // Initialize selected owners for single item
  React.useEffect(() => {
    if (!isBulkUpdate && entity?.ownerIds) {
      setSelectedOwners(entity.ownerIds);
    }
  }, [isBulkUpdate, entity]);

  /**
   * Handles the ownership update confirmation action.
   * Determines whether to perform single or bulk update and executes the appropriate logic.
   *
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const updateOwnershipAction = React.useCallback(async () => {
    setLoadingState(true);

    // Handle bulk update with custom handler
    if (isBulkUpdate && onBulkUpdate) {
      await onBulkUpdate(entity, selectedOwners);
      onFinish();
      onUpdate(selectedOwners);
      setLoadingState(false);
      return;
    }

    // Handle bulk update with default behavior
    if (isBulkUpdate) {
      // This will be handled by the parent component through onBulkUpdate
      onFinish();
      onUpdate(selectedOwners);
      setLoadingState(false);
      return;
    }

    // Handle single item update
    onFinish();
    onUpdate(selectedOwners);
    setLoadingState(false);
  }, [entity, selectedOwners, isBulkUpdate, onBulkUpdate, onFinish, onUpdate]);

  /**
   * Handles the dismiss/cancel action.
   * Closes the popup without performing any update.
   *
   * @function
   * @returns {void}
   */
  const dismiss = React.useCallback(() => {
    onFinish();
    onDismiss();
  }, [onFinish, onDismiss]);

  /**
   * Handles owner selection change.
   *
   * @function
   * @param {string} name - Field name (unused but required by MultipleOwnersSelector)
   * @param {Array<string>} ownerIds - Selected owner IDs
   * @returns {void}
   */
  const handleOwnerChange = React.useCallback((name, ownerIds) => {
    setSelectedOwners(ownerIds || []);
  }, []);

  const title = isBulkUpdate ? lang.BULK_TITLE : lang.TITLE;
  const contentText = isBulkUpdate
    ? lang.BULK_TEXT.replace('{count}', formatNumber(itemCount))
    : lang.TEXT.replace('{name}', entity?.title || 'this item');

  return (
    <Modal
      title={title}
      open={open}
      onCancel={dismiss}
      onSubmit={updateOwnershipAction}
      cancelText={lang.DISMISS}
      submitText={lang.CONFIRM}
      loading={isLoading}
      submitDisabled={isLoading || selectedOwners.length === 0}
      closeIcon={false}>
      <p className={styles.content}>{contentText}</p>
      <MultipleOwnersSelector
        value={selectedOwners}
        onChange={handleOwnerChange}
        name="ownerIds"
        disableAutoFill={true}
        variant="outlined"
      />
    </Modal>
  );
});

export { KnowledgeOwnerPopup };
